import {initContract} from '@ts-rest/core';
import {DB_SCHEMAS} from '../db/schemaExports';
import {z} from 'zod';

const c = initContract();

export const discoveredMcpToolSchema = z.object({
  name: z.string(),
  title: z.string().optional(),
  description: z.string()
});

// `auth_header_ciphertext` is never surfaced to clients — the
// presence of an auth header is exposed via the boolean `hasAuthHeader`
// so the UI can show whether the server is authed, without leaking the
// (encrypted) value. Plaintext is only accepted on write.
const baseResponseShape = DB_SCHEMAS.selectTenantMcpServer.omit({authHeaderCiphertext: true}).extend({
  discoveredTools: z.array(discoveredMcpToolSchema),
  hasAuthHeader: z.boolean()
});

export const tenantMcpServersContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: z.object({tenantId: z.string()}),
      responses: {
        200: z.array(baseResponseShape),
        403: z.object({message: z.string()})
      }
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: z.object({id: z.string()}),
      responses: {
        200: baseResponseShape,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      }
    },
    create: {
      method: 'POST',
      path: '/',
      body: z.object({
        tenantId: z.string().uuid(),
        displayName: z.string().min(1).max(100),
        url: z.string().url(),
        transport: z.enum(['http_stream']).default('http_stream'),
        // Full literal Authorization header value (e.g. "Bearer abc123").
        // Server encrypts at rest and never returns it.
        authHeader: z.string().optional(),
        enabled: z.boolean().default(true)
      }),
      responses: {
        201: baseResponseShape,
        400: z.object({message: z.string()}),
        403: z.object({message: z.string()})
      }
    },
    update: {
      method: 'PUT',
      path: '/:id',
      pathParams: z.object({id: z.string()}),
      body: z.object({
        displayName: z.string().min(1).max(100).optional(),
        url: z.string().url().optional(),
        transport: z.enum(['http_stream']).optional(),
        // Pass a string to replace the stored auth header (re-encrypted),
        // pass `null` to clear it, omit to leave unchanged.
        authHeader: z.string().nullable().optional(),
        enabled: z.boolean().optional()
      }),
      responses: {
        200: baseResponseShape,
        400: z.object({message: z.string()}),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      }
    },
    delete: {
      method: 'DELETE',
      path: '/:id',
      pathParams: z.object({id: z.string()}),
      body: null,
      responses: {
        200: baseResponseShape,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      }
    },
    rediscover: {
      method: 'POST',
      path: '/:id/rediscover',
      pathParams: z.object({id: z.string()}),
      body: z.object({}).optional(),
      responses: {
        200: baseResponseShape,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()}),
        502: z.object({message: z.string()})
      }
    }
  },
  {
    pathPrefix: '/tenant-mcp-servers'
  }
);
